import { Formik } from 'formik';
import _ from 'lodash';
import React from 'react';
import * as Yup from 'yup';
import {
  Button,
  Currency,
  DeleteConfirmation,
  Field,
  Icon,
  ListView,
  ListViewActions,
  ListViewMenu,
  Form,
} from '~/components';
import { useApi, useConfirmation, useMember, useToast, useWorkspace } from '~/contexts';
import { useEditableRow, useForm } from '~/hooks';
import { memberCostMethods } from '~/lookups';
import { emptyStringToNull } from '~/utils';
import { DaysInPeriod, PeriodDateRange, PeriodDateRangeField } from '../components';
import { periodStartDateValidator } from '../utils';
import ListViewActionsSuccess from '../components/ListViewActionsSuccess';
import ListViewActionsLoading from '../components/ListViewActionsLoading';

function CostPeriodRow({ member, period, periods, disableActions, isEditing, onEdit, onCancel, onSaved, onDeleted }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const toast = useToast();
  const [rowState, row] = useEditableRow();
  const [formState, form] = useForm();
  const currentMember = useMember();

  async function handleSubmit(values) {
    try {
      form.submit();
      const body = emptyStringToNull(values);
      const { data } = await api.www
        .workspaces(workspace.id)
        .members(member.id)
        .costPeriods(period ? period.id : undefined)
        .upsert(body);
      await onSaved(data);
      form.done();
      row.save();
      if (currentMember.isCurrent(member)) {
        await currentMember.refresh();
      }
    } catch ({ message }) {
      form.error({ message });
      toast.error(message);
    }
  }

  async function handleDelete() {
    try {
      await api.www
        .workspaces(workspace.id)
        .members(member.id)
        .costPeriods(period ? period.id : undefined)
        .delete();
      await onDeleted(period.id);
      if (currentMember.isCurrent(member)) {
        await currentMember.refresh();
      }
    } catch ({ message }) {
      toast.error(message);
    }
  }

  const isDefault = period.isDefault;

  if (!isEditing)
    return (
      <CostPeriodRowDetails
        period={period}
        member={member}
        saved={rowState.saved}
        disableActions={disableActions}
        onEdit={onEdit}
        onDelete={handleDelete}
        isDefault={isDefault}
      />
    );

  return (
    <CostPeriodRowForm
      period={period}
      periods={periods}
      member={member}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      isDefault={isDefault}
      {...formState}
    />
  );
}

function CostPeriodRowDetails({
  period,
  period: { id, currency, currencyName, costPerHour, overheadCostPerHour, totalCostPerHour },
  member,
  isDefault,
  saved,
  disableActions,
  onEdit,
  onDelete,
}) {
  const confirmation = useConfirmation();

  if (!id)
    return (
      <ListView.Row isDisabled={disableActions}>
        <ListView.Cell>
          <Button isAnchor isStrong disabled={disableActions} onClick={onEdit}>
            <Icon icon="plus" size="xs" spaceRight />
            Quick Add
          </Button>
        </ListView.Cell>
        <ListView.Cell />
        <ListView.Cell />
        <ListView.Cell />
        <ListView.Cell />
        <ListView.Cell />
        <ListView.Cell />
      </ListView.Row>
    );

  const handleDelete = async () => {
    const confirm = await confirmation.prompt((resolve) => (
      <DeleteConfirmation resolve={resolve}>Are you sure you want to delete this cost period?</DeleteConfirmation>
    ));
    if (!confirm) return;

    onDelete();
  };

  return (
    <ListView.Row isDisabled={disableActions}>
      <ListView.Cell>
        <PeriodDateRange period={period} member={member} />
      </ListView.Cell>
      <ListView.Cell>
        {currencyName} ({currency})
      </ListView.Cell>
      <ListView.Cell>{memberCostMethods[period.costMethodId].name}</ListView.Cell>
      <ListView.Cell>
        {
          {
            hourly: <Currency value={costPerHour} currency={currency} />,
            fixed_hourly: <Currency value={costPerHour} currency={currency} />,
            fixed_monthly: <Currency value={period.costPerMonth} currency={currency} />,
          }[period.costMethodId]
        }
      </ListView.Cell>
      <ListView.Cell>
        {
          {
            hourly: <Currency value={overheadCostPerHour} currency={currency} />,
            fixed_hourly: <Currency value={overheadCostPerHour} currency={currency} />,
            fixed_monthly: <Currency value={period.overheadCostPerMonth} currency={currency} />,
          }[period.costMethodId]
        }
      </ListView.Cell>
      <ListView.Cell>
        {
          {
            hourly: <Currency value={totalCostPerHour} currency={currency} />,
            fixed_hourly: <Currency value={totalCostPerHour} currency={currency} />,
            fixed_monthly: <Currency value={period.totalCostPerMonth} currency={currency} />,
          }[period.costMethodId]
        }
      </ListView.Cell>
      <ListView.Cell>
        <DaysInPeriod period={period} member={member} />
      </ListView.Cell>
      <ListViewActions>
        {saved ? (
          <ListViewActionsSuccess />
        ) : (
          <>
            <ListViewActions.Edit disabled={disableActions} onClick={onEdit} />
            <hr />
            <ListViewMenu>
              {({ setIsOpen }) => (
                <>
                  <ListViewMenu.Item onClick={() => setIsOpen(false) || onEdit()}>Edit</ListViewMenu.Item>
                  <ListViewMenu.Item
                    disabled={isDefault}
                    tooltip={isDefault ? 'The default period cannot be deleted.' : undefined}
                    onClick={() => setIsOpen(false) || handleDelete()}>
                    Delete
                  </ListViewMenu.Item>
                </>
              )}
            </ListViewMenu>
          </>
        )}
      </ListViewActions>
    </ListView.Row>
  );
}

function CostPeriodRowForm({ period, periods, isSubmitting, onSubmit, onCancel, member, isDefault }) {
  const { workspace } = useWorkspace();

  const initialValues = {
    // Only the default period may have a null start date
    start: period.start === undefined ? new Date() : period.start,
    currency: period.currency || workspace.currency,
    costMethodId: period.costMethodId || member.costMethodId || 'fixed_hourly',
    costPerHour: period.costPerHour || '',
    overheadCostPerHour: period.overheadCostPerHour || '',
    costPerMonth: period.costPerMonth || '',
    overheadCostPerMonth: period.overheadCostPerMonth || '',
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={Yup.object().shape({
        costPerHour: Yup.number().label('Labor Cost per Hour').min(0).max(99999999999).nullable(),
        overheadCostPerHour: Yup.number().label('Overhead Cost per Hour').min(0).max(99999999999).nullable(),
        costPerMonth: Yup.number().label('Labor Cost per Month').min(0).max(99999999999).nullable(),
        overheadCostPerMonth: Yup.number().label('Overhead Cost per Month').min(0).max(99999999999).nullable(),
        start: periodStartDateValidator({ periods, period, member }),
      })}>
      {({ values }) => {
        let totalPerHour = null;
        if (typeof values.costPerHour === 'number' || typeof values.overheadCostPerHour === 'number') {
          totalPerHour = (values.costPerHour || 0) + (values.overheadCostPerHour || 0);
        }

        let totalPerMonth = null;
        if (typeof values.costPerMonth === 'number' || typeof values.overheadCostPerMonth === 'number') {
          totalPerMonth = (values.costPerMonth || 0) + (values.overheadCostPerMonth || 0);
        }
        return (
          <Form>
            <ListView.Row focused style={{ padding: '3px 0 3px 0' }}>
              <ListView.Cell>
                <PeriodDateRangeField
                  autoFocus={!period.id}
                  periods={periods}
                  period={period}
                  member={member}
                  start={values.start}
                  isDefault={isDefault}
                />
              </ListView.Cell>
              <ListView.Cell>
                <Field.WorkspaceCurrencySelect autoFocus={!!period.id} name="currency" />
              </ListView.Cell>
              <ListView.Cell>
                <div style={{ width: '100%' }}>
                  <Field.SingleSelect name="costMethodId" placeholder="Cost Method">
                    {_.map(memberCostMethods, (method) => (
                      <option key={method.id} value={method.id}>
                        {method.name}
                      </option>
                    ))}
                  </Field.SingleSelect>
                </div>
              </ListView.Cell>
              <ListView.Cell>
                {
                  {
                    hourly: (
                      <Field.Currency name="costPerHour" placeholder="Labor Cost per Hour" currency={values.currency} />
                    ),
                    fixed_hourly: (
                      <Field.Currency name="costPerHour" placeholder="Labor Cost per Hour" currency={values.currency} />
                    ),
                    fixed_monthly: (
                      <Field.Currency
                        name="costPerMonth"
                        placeholder="Labor Cost per Month"
                        currency={values.currency}
                      />
                    ),
                  }[values.costMethodId]
                }
              </ListView.Cell>
              <ListView.Cell>
                {
                  {
                    hourly: (
                      <Field.Currency
                        name="overheadCostPerHour"
                        placeholder="Overhead Cost per Hour"
                        currency={values.currency}
                      />
                    ),
                    fixed_hourly: (
                      <Field.Currency
                        name="overheadCostPerHour"
                        placeholder="Overhead Cost per Hour"
                        currency={values.currency}
                      />
                    ),
                    fixed_monthly: (
                      <Field.Currency
                        name="overheadCostPerMonth"
                        placeholder="Overhead Cost per Month"
                        currency={values.currency}
                      />
                    ),
                  }[values.costMethodId]
                }
              </ListView.Cell>

              <ListView.Cell>
                {
                  {
                    hourly: <Currency value={totalPerHour} currency={values.currency} />,
                    fixed_hourly: <Currency value={totalPerHour} currency={values.currency} />,
                    fixed_monthly: <Currency value={totalPerMonth} currency={values.currency} />,
                  }[values.costMethodId]
                }
              </ListView.Cell>

              <ListView.Cell>
                <DaysInPeriod periods={periods} period={period} member={member} start={values.start} />
              </ListView.Cell>
              <ListViewActions>
                {isSubmitting ? (
                  <ListViewActionsLoading />
                ) : (
                  <>
                    <Button type="submit" data-testid="submit_button">
                      <Icon icon="check" />
                    </Button>
                    <hr />
                    <ListViewActions.Delete icon="times" onClick={onCancel} />
                  </>
                )}
              </ListViewActions>
            </ListView.Row>
          </Form>
        );
      }}
    </Formik>
  );
}

export default CostPeriodRow;
